import React, { useMemo, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ListTodo } from 'lucide-react';
import { useMediaQuery } from '../../../hooks/useIsSmallScreen';
import { UnifiedTask } from './TaskCard';
import CalendarChip, { CalendarChipPreview, isDelmoment } from './CalendarChip';
import { buildMonthEntries, CalendarEntry } from '../utils/calendarEntries';
import {
    addMonths,
    CalendarDay,
    CalendarMonth,
    dayLabel,
    monthLabel,
    monthOf,
    monthWeeks,
    sameMonth,
    toDayKey,
} from '../utils/calendarMonth';
import './ManagementCalendar.css';

interface ManagementCalendarProps {
    /** Open work orders with a dueDate and active kontroller, already filtered. */
    planned: UnifiedTask[];
    /** Completed work orders of the visible month's year, already filtered. */
    completed: UnifiedTask[];
    /** Open work orders without a dueDate, already filtered. */
    unplanned: UnifiedTask[];
    loading: boolean;
    month: CalendarMonth;
    onMonthChange: (month: CalendarMonth) => void;
    onOpen: (entry: CalendarEntry) => void;
    /** A work order was dropped on `day`; the page confirms in the due date modal. */
    onReschedule: (task: UnifiedTask, day: string) => void;
    getUserName: (uid: string) => string;
    /** Phones get a day-by-day list without drag. */
    agenda: boolean;
}

const WEEKDAYS = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
const DAY_DROP_PREFIX = 'day:';

const unplannedEntry = (task: UnifiedTask): CalendarEntry => ({
    id: `open:${task.key}`,
    kind: 'open',
    day: '',
    overdue: false,
    task,
});

/**
 * Förvaltning as a month: open work orders on their due date, kontroller on their
 * next date and completed work orders on their completion date. Open work orders
 * are dragged to another day (or from Ej planerade onto one); the page confirms.
 */
const ManagementCalendar: React.FC<ManagementCalendarProps> = ({
    planned, completed, unplanned, loading, month, onMonthChange, onOpen, onReschedule, getUserName, agenda,
}) => {
    const today = toDayKey(new Date());
    const currentMonth = monthOf(new Date());
    const isCurrentMonth = sameMonth(month, currentMonth);

    const byDay = useMemo(() => buildMonthEntries(planned, completed, month), [planned, completed, month]);
    const weeks = useMemo(() => monthWeeks(month), [month]);
    const unplannedEntries = useMemo(() => unplanned.map(unplannedEntry), [unplanned]);
    // Delmoment only appear when the board opts in to them; the legend follows
    const hasDelmoment = useMemo(
        () => unplannedEntries.some(isDelmoment) || Array.from(byDay.values()).some(entries => entries.some(isDelmoment)),
        [byDay, unplannedEntries],
    );

    const [dragged, setDragged] = useState<CalendarEntry | null>(null);
    const [unplannedOpen, setUnplannedOpen] = useState(true);
    const canHover = !useMediaQuery('(hover: none)');
    const showTooltip = canHover && !dragged;

    // A small move threshold keeps a plain click a click
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragStart = (event: DragStartEvent) => {
        setDragged((event.active.data.current?.entry as CalendarEntry | undefined) ?? null);
    };
    const handleDragEnd = (event: DragEndEvent) => {
        setDragged(null);
        const entry = event.active.data.current?.entry as CalendarEntry | undefined;
        const overId = event.over?.id;
        if (!entry || typeof overId !== 'string' || !overId.startsWith(DAY_DROP_PREFIX)) return;
        const day = overId.slice(DAY_DROP_PREFIX.length);
        if (day !== entry.day) onReschedule(entry.task, day);
    };

    const chipProps = { onOpen, getUserName, showTooltip, dragDisabled: agenda };

    const nav = (
        <div className="calendar-nav">
            <div className="calendar-nav__month">
                <Button variant="outline-secondary" size="sm" aria-label="Föregående månad" onClick={() => onMonthChange(addMonths(month, -1))}>
                    <ChevronLeft size={18} aria-hidden="true" />
                </Button>
                <h2 className="calendar-nav__label">{monthLabel(month)}</h2>
                <Button variant="outline-secondary" size="sm" aria-label="Nästa månad" onClick={() => onMonthChange(addMonths(month, 1))}>
                    <ChevronRight size={18} aria-hidden="true" />
                </Button>
                <Button variant="outline-secondary" size="sm" disabled={isCurrentMonth} onClick={() => onMonthChange(currentMonth)}>
                    Idag
                </Button>
                {/* Always rendered, only hidden, so the controls never shift */}
                <span className="calendar-nav__loading" aria-hidden={!loading} style={{ visibility: loading ? 'visible' : 'hidden' }}>
                    Laddar…
                </span>
            </div>
            <ul className="calendar-legend" aria-label="Förklaring">
                <li><span className="calendar-legend__swatch calendar-legend__swatch--open" />Ärende</li>
                {hasDelmoment && <li><span className="calendar-legend__swatch calendar-legend__swatch--delmoment" />Delmoment</li>}
                <li><span className="calendar-legend__swatch calendar-legend__swatch--inspection" />Kontroll</li>
                <li><span className="calendar-legend__swatch calendar-legend__swatch--completed" />Klart</li>
                <li><span className="calendar-legend__swatch calendar-legend__swatch--overdue" />Försenat</li>
            </ul>
        </div>
    );

    if (agenda) {
        const days = Array.from(byDay.keys()).sort();
        return (
            <div className="management-calendar management-calendar--agenda">
                {nav}
                <div className="calendar-zone calendar-agenda">
                    {days.length === 0 && !loading && (
                        <p className="calendar-empty">Inget planerat eller slutfört i {monthLabel(month)}.</p>
                    )}
                    {days.map(day => (
                        <section key={day} className={`calendar-agenda-day${day === today ? ' calendar-agenda-day--today' : ''}`}>
                            <h3 className="calendar-agenda-day__label">
                                {dayLabel(day)}
                                {day === today && <span className="calendar-agenda-day__today">Idag</span>}
                            </h3>
                            {byDay.get(day)!.map(entry => (
                                <CalendarChip key={entry.id} entry={entry} {...chipProps} showBuilding />
                            ))}
                        </section>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDragged(null)}>
            <div className="management-calendar">
                <div className="management-calendar__main">
                    {nav}
                    <div className="calendar-zone calendar-grid" role="grid" aria-label={monthLabel(month)}>
                        <div className="calendar-grid__head" role="row">
                            <span className="calendar-grid__week-head" role="columnheader">v.</span>
                            {WEEKDAYS.map(d => <span key={d} className="calendar-grid__weekday" role="columnheader">{d}</span>)}
                        </div>
                        {weeks.map(week => (
                            <div key={week.days[0].date} className="calendar-grid__week" role="row">
                                <span className="calendar-grid__week-number" role="rowheader">{week.weekNumber}</span>
                                {week.days.map(day => (
                                    <DayCell key={day.date} day={day} isToday={day.date === today}>
                                        {day.inMonth && byDay.get(day.date)?.map(entry => (
                                            <CalendarChip key={entry.id} entry={entry} {...chipProps} />
                                        ))}
                                    </DayCell>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                <aside className={`calendar-zone calendar-unplanned${unplannedOpen ? '' : ' calendar-unplanned--closed'}`}>
                    <button
                        type="button"
                        className="calendar-unplanned__toggle"
                        aria-expanded={unplannedOpen}
                        aria-label={`Ej planerade (${unplannedEntries.length})`}
                        onClick={() => setUnplannedOpen(open => !open)}
                    >
                        <ListTodo size={20} aria-hidden="true" />
                        <span className="calendar-unplanned__title">Ej planerade</span>
                        <Badge bg="secondary">{unplannedEntries.length}</Badge>
                        {unplannedOpen ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                    </button>
                    {unplannedOpen && (
                        <>
                            <p className="calendar-unplanned__hint">Dra ett ärende till en dag för att planera det.</p>
                            <div className="calendar-unplanned__list">
                                {unplannedEntries.length === 0 && <p className="calendar-empty">Inga oplanerade ärenden</p>}
                                {unplannedEntries.map(entry => (
                                    <CalendarChip key={entry.id} entry={entry} {...chipProps} showBuilding />
                                ))}
                            </div>
                        </>
                    )}
                </aside>
            </div>
            <DragOverlay dropAnimation={null}>
                {dragged && <CalendarChipPreview entry={dragged} />}
            </DragOverlay>
        </DndContext>
    );
};

/** A day of the grid; days of the month take drops. */
const DayCell: React.FC<{ day: CalendarDay; isToday: boolean; children: React.ReactNode }> = ({ day, isToday, children }) => {
    const { setNodeRef, isOver } = useDroppable({ id: `${DAY_DROP_PREFIX}${day.date}`, disabled: !day.inMonth });
    const className = [
        'calendar-day',
        day.inMonth ? '' : 'calendar-day--outside',
        isToday ? 'calendar-day--today' : '',
        isOver ? 'calendar-day--drop' : '',
    ].filter(Boolean).join(' ');
    return (
        <div ref={setNodeRef} className={className} role="gridcell" aria-label={dayLabel(day.date)}>
            <span className="calendar-day__number">{day.dayOfMonth}</span>
            <div className="calendar-day__items">{children}</div>
        </div>
    );
};

export default ManagementCalendar;
